import { useAsync } from "./useAsync";
import {
  LeaveCreateDTO,
  LeaveCreateResponse,
  LeaveDto,
  LeaveStats,
  LeaveAdminFullUpdateDTO,
} from "../types/leaves.types";
import leaveService from "../services/leave/leave.service";
import { useMemo, useCallback } from "react";

export const useLeaves = () => {
  const {
    data: leaves,
    loading,
    error: leavesError,
    execute,
  } = useAsync<LeaveDto[]>();

  const fetchLeaves = useCallback(async () => {
    return execute(() => leaveService.getLeaves());
  }, [execute]);

  const stats: LeaveStats = useMemo(() => {
    if (!leaves) return { total: 0, approved: 0, pending: 0, rejected: 0, pending_hierarchy_approval: 0 };

    return leaves.reduce(
      (acc, leave) => {
        acc.total++;
        const status = leave.status;
        if (status === 'pending' || status === 'pending_hierarchy_approval') {
          acc.pending++;
        } else if (status === 'approved') {
          acc.approved++;
        } else if (status === 'rejected') {
          acc.rejected++;
        }
        return acc;
      },
      { total: 0, approved: 0, pending: 0, rejected: 0, pending_hierarchy_approval: 0 }
    );
  }, [leaves]);

  const updateLeaveStatus = async (
    leaveId: number,
    status: { status: string }
  ) => {
    await leaveService.updateLeave(leaveId, status);
    await fetchLeaves();
  };

  const createLeave = async (leave: LeaveCreateDTO): Promise<LeaveCreateResponse> => {
    const response = await leaveService.createLeaveAdmin(leave);
    await fetchLeaves();
    return response;
  };

  const updateLeaveByAdmin = async (
    leaveId: number,
    leave: LeaveAdminFullUpdateDTO
  ) => {
    await leaveService.updateLeaveByAdmin(leaveId, leave);
    await fetchLeaves();
  };

  const cancelLeave = async (leaveId: number) => {
    await leaveService.cancelLeave(leaveId);
    await fetchLeaves();
  };

  return {
    leaves,
    stats,
    updateLeaveStatus,
    updateLeaveByAdmin,
    cancelLeave,
    loading,
    leavesError,
    fetchLeaves,
    createLeave,
  };
};
